import { StandardEvents } from '@shopify/events';

function openCart() {
  const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
  if (trigger) trigger.click();
  else window.location.assign(Theme.routes.cart_url);
}

function measureDock(element) {
  const measure = () => document.body.style.setProperty('--purchase-dock-height', `${Math.ceil(element.getBoundingClientRect().height) + 20}px`);
  const observer = new ResizeObserver(measure);
  observer.observe(element);
  measure();
  return observer;
}

class PurchaseDock extends HTMLElement {
  #revision = 0;
  #observer;
  #emptySlots;

  connectedCallback() {
    this.#observer = measureDock(this);
    if (this.dataset.kind !== 'cases') return;
    this.#emptySlots = Array.from(this.querySelectorAll('[data-slot]'), slot => slot.firstElementChild.cloneNode(true));
    this.addEventListener('click', this.#click, true);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#refresh);
    this.#refresh();
  }

  disconnectedCallback() {
    this.#revision++;
    this.#observer?.disconnect();
    this.removeEventListener('click', this.#click, true);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#refresh);
  }

  #click = (event) => {
    const slot = event.target.closest('[data-slot]');
    if (slot?.dataset.inCart === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      openCart();
    }
  };

  #refresh = async (event) => {
    const revision = ++this.#revision;
    try {
      await event?.promise;
      const response = await fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Cart response ${response.status}`);
      const cart = await response.json();
      if (!this.isConnected || revision !== this.#revision) return;
      const builder = this.querySelector('case-pair-selector');
      await customElements.whenDefined('case-pair-selector');
      if (!this.isConnected || revision !== this.#revision) return;
      const eligible = cart.items.filter(item => {
        const card = builder.querySelector(`[data-design-card="${item.product_id}"]`);
        return Array.from(card?.querySelector('[data-variants]')?.options || []).some(option =>
          option.value === String(item.variant_id) && Number(option.dataset.price) === Number(builder.dataset.price));
      });
      builder.cartVariantIds = new Set(eligible.map(item => String(item.variant_id)));
      const slots = Array.from(builder.querySelectorAll('[data-slot]'));
      const pending = slots.filter(slot => slot.dataset.variant && slot.dataset.inCart !== 'true')
        .map(slot => ({ product_id: slot.dataset.design, variant_id: slot.dataset.variant, pending: true }));
      const choices = [];
      for (const item of [...eligible, ...pending]) {
        if (!choices.some(choice => String(choice.product_id) === String(item.product_id))) choices.push(item);
        if (choices.length === 2) break;
      }
      slots.forEach((slot, index) => {
        const item = choices[index];
        if (!item) {
          delete slot.dataset.design;
          delete slot.dataset.variant;
          delete slot.dataset.inCart;
          slot.replaceChildren(this.#emptySlots[index].cloneNode(true));
          return;
        }
        slot.dataset.design = String(item.product_id);
        slot.dataset.variant = String(item.variant_id);
        slot.dataset.inCart = item.pending ? 'false' : 'true';
        const template = builder.querySelector(`template[data-design="${item.product_id}"]`);
        slot.replaceChildren(template.content.cloneNode(true));
        const option = builder.querySelector(`[data-design-card="${item.product_id}"] [data-variants] option[value="${item.variant_id}"]`);
        slot.querySelector('[data-variant-summary]').textContent = option?.textContent || '';
        const title = slot.querySelector('strong').textContent;
        const button = slot.querySelector('button');
        button.setAttribute('aria-label', `${item.pending ? 'Change' : 'Review in bag'} ${title}, ${option?.textContent || ''}`);
        if (item.featured_image?.url) slot.querySelector('img').src = item.featured_image.url;
      });
      builder.update();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[purchase-dock] Cart refresh failed:', error);
    }
  };
}

if (!customElements.get('purchase-dock')) customElements.define('purchase-dock', PurchaseDock);

for (const sticky of document.querySelectorAll('sticky-add-to-cart')) {
  measureDock(sticky.querySelector('[ref="stickyBar"]'));
  if (!sticky.hasAttribute('data-case-product')) continue;
  const section = sticky.closest('.shopify-section');
  const review = () => {
    const dialog = sticky.querySelector('.sticky-case-selection');
    if (!dialog) return;
    const items = dialog.querySelector('[data-selection-items]');
    const first = document.createElement('div');
    first.className = 'sticky-case-selection__row';
    const image = sticky.querySelector('[ref="productImage"]');
    if (image) {
      const thumbnail = image.cloneNode(true);
      thumbnail.removeAttribute('ref');
      thumbnail.removeAttribute('data-testid');
      first.append(thumbnail);
    }
    const copy = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = sticky.querySelector('.sticky-add-to-cart__title').textContent;
    const variant = document.createElement('small');
    variant.textContent = sticky.querySelector('.sticky-add-to-cart__variant').textContent;
    copy.append(title, variant);
    first.append(copy);
    items.replaceChildren(first);
    if (sticky.hasAttribute('data-pair-selected')) {
      for (const selected of section.querySelectorAll('case-pair-selector [data-extra-case] .case-pair__selected:not(.case-pair__choose)')) {
        const second = document.createElement('div');
        second.append(...Array.from(selected.childNodes, node => node.cloneNode(true)));
        second.className = 'sticky-case-selection__row';
        second.querySelector('button')?.remove();
        items.append(second);
      }
    }
    dialog.showModal();
  };
  sticky.addEventListener('click', event => {
    const dialog = sticky.querySelector('.sticky-case-selection');
    if (event.target.closest('[data-close-selection]')) dialog.close();
    if (event.target.closest('[data-edit-selection]')) {
      dialog.close();
      const target = section.querySelector('variant-picker');
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.querySelector('input:checked, select')?.focus({ preventScroll: true });
    }
    if (event.target.closest('[data-sticky-selection]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const extra = event.target.closest('[data-sticky-second-case], [data-sticky-third-case]');
      if (extra && !extra.querySelector('img')) {
        const number = extra.hasAttribute('data-sticky-third-case') ? '3' : '2';
        section.querySelector('case-pair-selector [data-extra-case][data-case-number="' + number + '"] > [data-open-picker]').click();
      } else review();
      return;
    }
  }, true);
}
